(ns ui.view.kanban-opensearch.view
  (:require
   ["react" :as react]
   [clojure.set :as set]
   [clojure.string :as string]
   [common.localization :as loc]
   [common.opensearch.totals :as totals]
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.kanban-list.view :as kanban-list]
   [ui.components.virtuoso :as v]
   [ui.dom-globals :as dg]
   [ui.view.kanban-opensearch.event :as event]
   [ui.view.kanban-opensearch.model :as model]
   [ui.view.kanban-opensearch.outcome-tray :as outcome-tray]
   [ui.view.kanban-opensearch.sub :as sub]
   [ui.view.opensearch-cards.view :as cards]
   [ui.view.opensearch-value.format :as value-format]))

(def column-scroller
  (react/forwardRef
   (fn [props ref]
     (let [merged-style (js/Object.assign #js {} (or (.-style props) #js {}))
           next-props (js/Object.assign #js {} props)]
       (aset next-props "ref" ref)
       (aset next-props "style" merged-style)
       (react/createElement "div" next-props)))))

(defn- item-id
  [card]
  (or (get card "original.db/id")
      (get card :original.db/id)
      (:db/id card)
      (:id card)))

(defn- slice-values
  "Return a set of column values from `order` between indices [start-idx..end-idx] inclusive.
  Defensive: tolerate nil indices and any `order` seq type."
  [order start-idx end-idx]
  (let [order (if (vector? order) order (vec (or order [])))
        n (count order)
        start-idx (or start-idx 0)
        end-idx (or end-idx 0)
        start-idx (cu/clamp start-idx 0 (max 0 (dec n)))
        end-idx (cu/clamp end-idx 0 (max 0 (dec n)))
        end-excl (min n (inc end-idx))]
    (if (or (zero? n) (<= end-excl start-idx))
      #{}
      (into #{} (subvec order start-idx end-excl)))))

(defn- js-array-cache
  "A function that gives a JS array of a vector. It keeps the last array, so the same
  vector gives the same array at each render, and React does not get a new prop each
  time."
  []
  (let [last-call (volatile! nil)]
    (fn [values]
      (let [[last-values last-array] @last-call]
        (if (= last-values values)
          last-array
          (let [array (into-array values)]
            (vreset! last-call [values array])
            array))))))

(def ^:private height-estimates-array
  (js-array-cache))

(defn- card-placeholder
  []
  [:div
   {:class "tw2-rounded-3xl tw2-border tw2-border-dashed tw2-border-dropdown-border-default tw2-bg-bg-primary tw2-p-6 tw2-text-sm tw2-text-tertiary"}
   "Loading..."])

(defn- collapse-state
  [items collapsed-ids]
  (let [ids (set (keep item-id items))
        collapsed-visible-ids (set/intersection ids (set (or collapsed-ids #{})))]
    (cond
      (empty? ids) :all-expanded
      (empty? collapsed-visible-ids) :all-expanded
      (= (count ids) (count collapsed-visible-ids)) :all-collapsed
      :else :mixed)))

(defn- report-collapse-state!
  [all-visible-items ui-state on-collapse-state-changed]
  (when (fn? on-collapse-state-changed)
    (on-collapse-state-changed
     (collapse-state all-visible-items (:collapsed-ids ui-state)))))

(defn- collapse-state-with-column-collapses
  [all-visible-items ui-state cards-by-column]
  (let [collapsed-item-ids (into (set (or (:collapsed-ids ui-state) #{}))
                                 (mapcat (fn [column-value]
                                           (map item-id (get-in cards-by-column [column-value :items] [])))
                                         (or (:collapsed-columns ui-state) #{})))]
    (collapse-state all-visible-items collapsed-item-ids)))

(defn- visible-column-values
  [ordered-columns columns-lookup hide-empty-columns?]
  (let [ordered-columns (vec (or ordered-columns []))]
    (if hide-empty-columns?
      (->> ordered-columns
           (filter (fn [column-value]
                     (pos? (long (or (get-in columns-lookup [column-value :count]) 0)))))
           vec)
      ordered-columns)))

(defn- card-state-key
  [row-group-value column-value]
  (if (some? row-group-value)
    [row-group-value column-value]
    column-value))

(defn- toggle-collapsed!
  [ui-state* card]
  (when-let [id (item-id card)]
    (swap! ui-state*
           update
           :collapsed-ids
           (fn [collapsed-ids]
             (let [collapsed-ids (or collapsed-ids #{})]
               (if (contains? collapsed-ids id)
                 (disj collapsed-ids id)
                 (conj collapsed-ids id)))))))

(defn- toggle-collapsed-column!
  [ui-state* column-value]
  (swap! ui-state*
         update
         :collapsed-columns
         (fn [collapsed-columns]
           (let [collapsed-columns (or collapsed-columns #{})]
             (if (contains? collapsed-columns column-value)
               (disj collapsed-columns column-value)
               (conj collapsed-columns column-value))))))

(defn- sum-total
  "The total of `spec` in the `totals` of a column, a cell or a swimlane, see
   `common.opensearch.totals`. The id of `spec` names its field, so a total of the
   field that the board summed before is not found."
  [spec totals]
  (let [id (:id spec)]
    (when id
      (or (get totals id)
          (get totals (keyword id))))))

(defn- sum-text
  "The text of the sum of the field that the Kanban sums in `totals`, or nil.
   Without `totals`, the text of `total`, which `sum-total` gave."
  [kanban-id {:keys [totals total]}]
  (let [spec (first (:totals @(rf/subscribe [::sub/config kanban-id])))
        total (or total (sum-total spec totals))]
    (when (and spec total)
      (value-format/total-text {:data-type         (:data-type spec)
                                :total             total
                                :default-currency  @session/default-currency
                                :number-multiplier (:tenant/cur-multiplier @session/tenant)}))))

(defn- count-with-sum
  "The count of a header, with the sum after it when there is one."
  [count sum]
  (if sum
    (str count " · " sum)
    count))

(defn- more-cards-to-load?
  "A column loads its next page when it has more cards and a cursor. A page that is
  loading is not loaded again. A page that failed is not loaded again either. The
  list asks for the next page each time it is shown at its end, so a failed request
  came back in a loop when the browser had run out of resources (DEV-8218)."
  [{:keys [has-more? next-cursor status]}]
  (and (true? has-more?)
       (some? next-cursor)
       (not (contains? #{:loading :error} status))))

(defn kanban-column
  [_props]
  (fn [props]
    (let [{:keys [kanban-id card-config column-meta column-value row-group-value swimlane? cards-by-column on-card-clicked on-card-preview active-entity-id on-selection-changed
                  on-collapse-state-changed all-visible-items ui-state* drag-enabled? card-actions-fn
                  card-status-fn column-add-fn]} props
          card-key (card-state-key row-group-value column-value)
          collapsed-column-key card-key
          cards-sub (rf/subscribe [::sub/cards-in-column kanban-id card-key])
          paging-sub (rf/subscribe [::sub/column-state kanban-id card-key])
          cards (vec (or @cards-sub []))
          paging (or @paging-sub {})
          total (:total paging)
          loaded (count cards)
          ;; A cell of a swimlane has the sum of its own cards. The value of the
          ;; uncategorized swimlane is nil, so the value does not tell a swimlane.
          header-sum (sum-text kanban-id {:totals (if swimlane?
                                                    (:totals paging)
                                                    (get-in column-meta [:raw :totals]))})
          header-count (count-with-sum (or (:count column-meta) total loaded 0) header-sum)
          next-cursor (:next-cursor paging)
          can-load-more? (more-cards-to-load? paging)
          virtuoso-total (if can-load-more?
                           (inc loaded)
                           loaded)
          dragging (:dragging @ui-state*)
          drop-target-column (:drop-target-column @ui-state*)
          drop-active? (and dragging
                            (= drop-target-column collapsed-column-key)
                            drag-enabled?)
          selected-ids (:selected-ids @ui-state* #{})
          collapsed-ids (:collapsed-ids @ui-state* #{})
          collapsed-columns (:collapsed-columns @ui-state* #{})
          column-collapsed? (contains? collapsed-columns collapsed-column-key)
          column-title (or (:label column-meta)
                           (pr-str column-value))
          column-add (when (fn? column-add-fn)
                       (column-add-fn {:column-value column-value
                                       :column-meta  column-meta}))
          scroller-class "kanban-column-scroller tw2-overflow-y-auto tw2-overflow-x-hidden
            [-ms-overflow-style:auto]
            [scrollbar-color:transparent_transparent]
            [&::-webkit-scrollbar-thumb]:tw2-bg-transparent
            [&::-webkit-scrollbar-thumb]:tw2-rounded-full
            [&::-webkit-scrollbar-track]:tw2-bg-transparent
            hover:[scrollbar-color:rgba(148,163,184,0.7)_transparent]
            hover:[&::-webkit-scrollbar-thumb]:tw2-bg-slate-300/70"
          ;; A scrollbar that is always shown takes space on the right only. The
          ;; gutter on both edges keeps the same space on the two sides of a card,
          ;; in a column with or without a scrollbar.
          scroller-style {:height           "100%"
                          :scrollbar-width  "thin"
                          :scrollbar-gutter "stable both-edges"}]
      [kanban-list/view
       {:title              column-title
        :count              header-count
        :collapsed?         column-collapsed?
        :drop-target?       (boolean drop-active?)
        :class              "kanban-column"
        :labels             {:collapse      (loc/tr :kanban-collapse-list)
                             :expand        (loc/tr :kanban-expand-list)
                             :add           (:label column-add)
                             :collapse-aria (loc/tr :kanban-collapse-list-aria [column-title])
                             :expand-aria   (loc/tr :kanban-expand-list-aria [column-title])
                             :add-aria      (loc/tr :kanban-add-to-list-aria [column-title])}
        :on-toggle-collapse (fn [_]
                              (let [next-state (toggle-collapsed-column! ui-state* collapsed-column-key)]
                                (when (fn? on-collapse-state-changed)
                                  (on-collapse-state-changed
                                   (collapse-state-with-column-collapses all-visible-items
                                                                         next-state
                                                                         cards-by-column)))))
        :on-add             (:on-click column-add)
        :section-props      {:on-mouse-enter (fn [_]
                                               (swap! ui-state* update :hovered-columns (fnil conj #{}) collapsed-column-key))
                             :on-mouse-leave (fn [_]
                                               (swap! ui-state* update :hovered-columns disj collapsed-column-key))
                             :on-drag-over   (fn [e]
                                               (when drag-enabled?
                                                 (dg/prevent-default e)
                                                 (swap! ui-state* assoc :drop-target-column collapsed-column-key)))
                             :on-drop        (fn [e]
                                               (when drag-enabled?
                                                 (dg/prevent-and-stop e)
                                                 (let [{:keys [card source-column-value]} (:dragging @ui-state*)]
                                                   (when (and card
                                                              (not= source-column-value column-value))
                                                     (rf/dispatch [::event/move-card
                                                                   {:kanban-id           kanban-id
                                                                    :card                card
                                                                    :source-column-value source-column-value
                                                                    :target-column-value column-value}]))
                                                   (swap! ui-state* outcome-tray/drag-ended))))}}
       [v/virtuoso
        {:style scroller-style
         :className scroller-class
         :components {:Scroller column-scroller}
         :totalCount virtuoso-total
         :overscan 6
         :horizontalDirection false
         :itemContent (fn [index]
                        (let [card (get cards index)
                              id (item-id card)
                              load-more-placeholder? (and can-load-more?
                                                          (= index loaded))]
                          (r/as-element
                           (cond
                             card
                             ^{:key (str "card-" index "-" id)}
                             [:div {:class "tw2-pb-2"}
                              [:div {:draggable (boolean (and drag-enabled? id))
                                     :title (when-not drag-enabled?
                                              (loc/tr :grid-cell-read-only))
                                     :class (if drag-enabled?
                                              "tw2-cursor-grab"
                                              "tw2-cursor-not-allowed tw2-opacity-90")
                                     :on-drag-start (fn [e]
                                                      (if-not (and drag-enabled? id)
                                                        (dg/prevent-and-stop e)
                                                        (do
                                                          (let [dt (dg/data-transfer e)]
                                                            (dg/data-transfer-set-data dt "text/plain" (str id))
                                                            (dg/data-transfer-set-effect-allowed dt "move"))
                                                          (swap! ui-state* assoc
                                                                 :dragging {:card card
                                                                            :source-column-value column-value}
                                                                 :drop-target-column column-value))))
                                     :on-drag-end (fn [_]
                                                    (swap! ui-state* outcome-tray/drag-ended))}
                               [cards/render-card {:card card
                                                   :card-config card-config
                                                   :search-words @(rf/subscribe [::sub/search-words kanban-id])
                                                   :actions (when (fn? card-actions-fn)
                                                              (card-actions-fn card))
                                                   :status (when (fn? card-status-fn)
                                                             (card-status-fn card))
                                                   :selected? (or (contains? selected-ids id)
                                                                  (and (some? active-entity-id)
                                                                       (= active-entity-id id)))
                                                   :collapsed? (contains? collapsed-ids id)
                                                   :on-click (fn [e clicked-card]
                                                               (if (and (fn? on-card-preview)
                                                                        (not (cards/modifier-click? e)))
                                                                 (on-card-preview clicked-card)
                                                                 (cards/select-card! ui-state* clicked-card all-visible-items on-selection-changed e)))
                                                   :on-double-click (fn [clicked-card]
                                                                      (when (fn? on-card-clicked)
                                                                        (on-card-clicked clicked-card)))
                                                   :on-toggle-collapse (fn [clicked-card]
                                                                         (let [next-state (toggle-collapsed! ui-state* clicked-card)]
                                                                           (report-collapse-state! all-visible-items
                                                                                                   next-state
                                                                                                   on-collapse-state-changed)))}]]]
                             load-more-placeholder?
                             ^{:key (str "load-more-" index)}
                             [card-placeholder]
                             :else
                             ^{:key (str "empty-" index)}
                             [:div {:style {:height "1px"}}]))))
         :endReached (fn [_last-index]
                       (when can-load-more?
                         (rf/dispatch
                          [::event/load-cards
                           {:kanban-id kanban-id
                            :row-group-value row-group-value
                            :column-value column-value
                            :start-index next-cursor
                            :count (or (:count paging) 50)}])))}]])))

(defn- restore-strip-scroll!
  "Restore the first column of a strip that is mounted again. `saved` is the column that the
  strip reported last, clamped to the columns that the strip has now. When that column is
  after the first, `schedule` runs a thunk on the next frame that calls `scroll-to!` with
  the column index. react-virtuoso does not honor `:initialTopMostItemIndex` on a horizontal
  remount, so the strip restores the column with `scrollToIndex` (DEV-8294)."
  [{:keys [saved column-count schedule scroll-to!]}]
  (let [idx (model/strip-start-index {:start saved :column-count column-count})]
    (when (pos? idx)
      (schedule (fn [] (scroll-to! idx))))))

(defn- kanban-column-strip
  "One horizontal strip of columns of a swimlane. The strip is virtualized, so a
  swimlane that scrolls out of view unmounts its strip and mounts it again at the first
  column. The strip keeps the column that it showed first in `strip-starts*`, outside the
  item, and restores it on mount.

  Two safeguards keep the restore reliable (DEV-8294): `:initialTopMostItemIndex` is not
  honored on a horizontal remount, so `scrollToIndex` restores the column imperatively on
  mount (`restore-strip-scroll!`); and the strip reports index 0 for a moment before the
  restore, so the writer keeps the saved column until the restore lands
  (`model/strip-start-write`)."
  [_props]
  (let [handle*     (atom nil)
        confirmed?* (atom false)
        ctx*        (atom nil)]
    (r/create-class
     {:display-name "kanban-column-strip"
      :component-did-mount
      (fn [_]
        (let [{:keys [strip-starts* row-group-value column-count]} @ctx*]
          (restore-strip-scroll!
           {:saved        (some-> strip-starts* deref (get row-group-value))
            :column-count column-count
            ;; Wait one frame, so the strip has a scroller to scroll.
            :schedule     (fn [f] (dg/request-animation-frame! (fn [_] (f))))
            :scroll-to!   (fn [idx] (when-let [h @handle*] (.scrollToIndex h idx)))})))
      :reagent-render
      (fn [{:keys [kanban-id card-config cards-by-column columns-lookup ordered-columns row-group-value swimlane?
                   on-card-clicked on-card-preview active-entity-id on-selection-changed on-collapse-state-changed all-visible-items ui-state* drag-enabled?
                   card-actions-fn card-status-fn column-add-fn strip-starts*]}]
        (let [n (count ordered-columns)
              buffer 2]
          (reset! ctx* {:strip-starts*   strip-starts*
                        :row-group-value row-group-value
                        :column-count    n})
          [v/virtuoso
           {:style {:height "100%" :width "100%"}
            :horizontalDirection true
            :totalCount n
            :overscan 2
            :ref (fn [h] (reset! handle* h))
            ;; A swimlane that scrolls out of view unmounts its strip. The strip then starts at the
            ;; column that it showed first when it was seen last, and not at the first column.
            ;; `scrollToIndex` on mount restores it too, because this prop is not honored on a
            ;; horizontal remount.
            :initialTopMostItemIndex (model/strip-start-index
                                      {:start        (some-> strip-starts* deref (get row-group-value))
                                       :column-count n})
            :rangeChanged (fn [rng]
                            (let [start (dg/start-index rng)
                                  end (dg/end-index rng)
                                  visible (slice-values ordered-columns start end)
                                  buffered (slice-values ordered-columns (- start buffer) (+ end buffer))]
                              (when strip-starts*
                                (let [write (model/strip-start-next
                                             {:saved        (get @strip-starts* row-group-value)
                                              :column-count n
                                              :incoming     start
                                              :confirmed?   @confirmed?*})]
                                  (reset! confirmed?* (:confirmed? write))
                                  (swap! strip-starts* assoc row-group-value (:value write))))
                              (rf/dispatch
                               [::event/set-visible-columns
                                {:kanban-id       kanban-id
                                 :row-group-value row-group-value
                                 :visible-values  visible
                                 :buffer-values   buffered}])))
            :itemContent (fn [index]
                     (let [col-value (get ordered-columns index)
                           card-key (card-state-key row-group-value col-value)
                           column-state (get cards-by-column card-key)
                           col-meta (cond-> (get columns-lookup col-value)
                                      swimlane?
                                      (assoc :count (:total column-state)))]
                       (r/as-element
                        [:div {:class "tw2-h-full tw2-mr-kanban-board-gap"}
                         ^{:key (str "kanban-column-" (pr-str row-group-value) "-" (pr-str col-value))}
                         [kanban-column {:kanban-id kanban-id
                                         :card-config card-config
                                         :column-meta col-meta
                                         :column-value col-value
                                         :row-group-value row-group-value
                                         :swimlane? swimlane?
                                         :cards-by-column cards-by-column
                                         :on-card-clicked on-card-clicked
                                         :on-card-preview on-card-preview
                                         :active-entity-id active-entity-id
                                         :on-selection-changed on-selection-changed
                                         :on-collapse-state-changed on-collapse-state-changed
                                         :all-visible-items all-visible-items
                                         :ui-state* ui-state*
                                         :drag-enabled? drag-enabled?
                                         :card-actions-fn card-actions-fn
                                         :card-status-fn card-status-fn
                                         :column-add-fn column-add-fn}]])))}]))})))

(defn- kanban-row-group
  "One swimlane. A swimlane with cards has a strip of columns, and the strip reports
  the columns that it shows. An empty swimlane is a header only: it has no strip and
  loads nothing."
  [{:keys [row-value row-meta strip-props]}]
  (let [row-label (or (:label row-meta)
                      (some-> row-value pr-str)
                      (loc/tr :uncategorized))
        row-count (or (:count row-meta) 0)
        row-sum (sum-text (:kanban-id strip-props) {:totals (get-in row-meta [:raw :totals])})
        header (fn [class]
                 [:div {:class class}
                  [:h3 {:class "tw2-text-sm tw2-font-semibold tw2-text-primary"}
                   row-label]
                  [:span {:class "tw2-text-xs tw2-text-tertiary"}
                   (count-with-sum (loc/tr-item-count row-count) row-sum)]])]
    (if (model/row-group-empty? row-meta)
      [:section {:class "tw2-rounded-[18px] tw2-border tw2-border-dropdown-border-default tw2-bg-bg-secondary tw2-px-3 tw2-py-2"}
       (header "tw2-flex tw2-items-baseline tw2-gap-2 tw2-px-1")]
      [:section {:class "tw2-min-h-[360px] tw2-rounded-[18px] tw2-border tw2-border-dropdown-border-default tw2-bg-bg-secondary tw2-p-3"}
       (header "tw2-mb-3 tw2-flex tw2-items-baseline tw2-gap-2 tw2-px-1")
       [:div {:class "tw2-h-[300px] tw2-min-h-0"}
        [kanban-column-strip (assoc strip-props :row-group-value row-value)]]])))

(defn- board-total
  "The line above the board: the number of the cards that the search finds, and
   the sum of the field that the Kanban sums, when it sums one. The columns
   endpoint counts all the cards once (`:board`). A card of a field with many
   values is in many columns, so the columns are added up only when there is no
   such count."
  [{:keys [kanban-id row-grouped?]}]
  (let [board @(rf/subscribe [::sub/board kanban-id])
        groups (vals (or @(rf/subscribe [(if row-grouped? ::sub/row-group-by-value ::sub/column-by-value)
                                         kanban-id])
                         {}))
        card-count (or (:count board)
                       (reduce + 0 (keep :count groups)))
        spec (first (:totals @(rf/subscribe [::sub/config kanban-id])))
        board-sum (when spec
                    (sum-text kanban-id {:total (or (sum-total spec (:totals board))
                                                    (totals/sum-of (:kind spec)
                                                                   (keep (fn [group]
                                                                           (sum-total spec (get-in group [:raw :totals])))
                                                                         groups)))}))]
    (when (pos? card-count)
      [:div {:class "tw2-px-1 tw2-text-xs tw2-text-tertiary"}
       (count-with-sum (loc/tr-item-count card-count) board-sum)])))

(defn no-visible-columns?
  "True when the board has no column to show, so the centered empty state replaces
   the column strip. A board that still has columns keeps them, also when they hold
   no card. A row-grouped board also has no column to show when it has no row group."
  [{:keys [ordered-columns row-grouped? ordered-row-groups]}]
  (or (zero? (count ordered-columns))
      (and row-grouped?
           (zero? (count ordered-row-groups)))))

(defn empty-board-props
  "The parts of the centered empty state. A filtered board shows the no-match
   message and the filter info. A board with no filter shows the nothing-yet
   message, and a create button when a create handler is given."
  [{:keys [filtered? on-create-first-item]}]
  {:message-key         (if filtered?
                          :grid-no-entities-found
                          :grid-no-entities-yet)
   :show-info?          (boolean filtered?)
   :show-create-button? (boolean (and (not filtered?)
                                      (fn? on-create-first-item)))})

(defn kanban-board
  [{:keys [kanban-id]}]
  (let [columns-order (rf/subscribe [::sub/column-order kanban-id])
        columns-by-val (rf/subscribe [::sub/column-by-value kanban-id])
        row-groups-order (rf/subscribe [::sub/row-group-order kanban-id])
        row-groups-by-val (rf/subscribe [::sub/row-group-by-value kanban-id])
        ;; The first column of each strip that was seen last. A plain atom: a write on each scroll
        ;; must not render anything.
        strip-starts* (atom {})]
    (fn [props]
      (let [{:keys [card-config on-card-clicked on-card-preview active-entity-id on-selection-changed on-collapse-state-changed
                    all-visible-items ui-state* drag-enabled? cards-by-column hide-empty-columns? row-group-by-field
                    card-actions-fn card-status-fn column-add-fn filtered? on-create-first-item]} props
            columns-lookup (or @columns-by-val {})
            row-groups-lookup (or @row-groups-by-val {})
            ordered-columns (visible-column-values @columns-order columns-lookup hide-empty-columns?)
            row-grouped? (some? row-group-by-field)
            ordered-row-groups (if row-grouped?
                                 (vec (or @row-groups-order []))
                                 [nil])]
        (cond
          ;; A board hides its empty columns (`hide-empty-columns?`), so an empty
          ;; pipeline can leave no column. A board that still has columns keeps
          ;; them, also when they hold no card (see the branches below). Show a
          ;; centered empty state here only when there is no column to show.
          (no-visible-columns? {:ordered-columns    ordered-columns
                                :row-grouped?       row-grouped?
                                :ordered-row-groups ordered-row-groups})
          (let [{:keys [message-key show-info? show-create-button?]}
                (empty-board-props {:filtered?            filtered?
                                    :on-create-first-item on-create-first-item})]
            [:div {:class "tw2-flex tw2-h-full tw2-min-h-0 tw2-w-full tw2-flex-col tw2-items-center tw2-justify-center tw2-gap-2 tw2-px-4 tw2-text-center tw2-text-sm tw2-text-tertiary"}
             [:div (loc/tr message-key)]
             (when show-info?
               [:div {:class "tw2-text-xs tw2-text-tertiary"}
                (loc/tr :new-grid-2023-no-entities-found-info)])
             (when show-create-button?
               [button/component
                {:variant  :secondary
                 :size     :sm
                 :class    "tw2-mt-1"
                 :on-click (fn [_]
                             (on-create-first-item))}
                (loc/tr :grid-no-entities-create-button)])])

          row-grouped?
          (let [strip-props {:kanban-id                 kanban-id
                             :card-config               card-config
                             :cards-by-column           cards-by-column
                             :columns-lookup            columns-lookup
                             :ordered-columns           ordered-columns
                             :on-card-clicked           on-card-clicked
                             :on-card-preview           on-card-preview
                             :active-entity-id          active-entity-id
                             :on-selection-changed      on-selection-changed
                             :on-collapse-state-changed on-collapse-state-changed
                             :all-visible-items         all-visible-items
                             :ui-state*                 ui-state*
                             :drag-enabled?             drag-enabled?
                             :card-actions-fn           card-actions-fn
                             :card-status-fn            card-status-fn
                             :column-add-fn             column-add-fn
                             :strip-starts*             strip-starts*
                             :swimlane?                 true}
                row-buffer 1]
            ;; The swimlanes are a vertical list. Only the swimlanes that are in or near view
            ;; are shown, and only their strips of columns load cards.
            [:div {:class "tw2-h-full tw2-min-h-0 tw2-w-full"}
             [v/virtuoso
              {:style             {:height "100%" :width "100%"}
               :className         "tw2-pr-2"
               :totalCount        (count ordered-row-groups)
               ;; No `:defaultItemHeight`: it makes Virtuoso skip the estimates.
               :heightEstimates   (height-estimates-array
                                   (model/row-group-height-estimates
                                    {:ordered-row-groups ordered-row-groups
                                     :row-groups-lookup  row-groups-lookup}))
               :overscan          200
               :rangeChanged      (fn [rng]
                                    (let [start (dg/start-index rng)
                                          end (dg/end-index rng)
                                          visible (slice-values ordered-row-groups start end)
                                          buffered (slice-values ordered-row-groups
                                                                 (- start row-buffer)
                                                                 (+ end row-buffer))]
                                      (rf/dispatch
                                       [::event/set-visible-rows
                                        {:kanban-id      kanban-id
                                         :visible-values visible
                                         :buffer-values  buffered}])))
               :itemContent       (fn [index]
                                    (let [row-value (get ordered-row-groups index)]
                                      (r/as-element
                                       [:div {:class "tw2-pb-4"}
                                        ^{:key (str "kanban-row-group-" (pr-str row-value))}
                                        [kanban-row-group {:row-value   row-value
                                                           :row-meta    (get row-groups-lookup row-value)
                                                           :strip-props strip-props}]])))}]])

          :else
          [:div
           {:class "tw2-h-full tw2-min-h-0 tw2-w-full tw2-overflow-hidden"}
           [kanban-column-strip {:kanban-id kanban-id
                                 :card-config card-config
                                 :cards-by-column cards-by-column
                                 :columns-lookup columns-lookup
                                 :ordered-columns ordered-columns
                                 :row-group-value nil
                                 :on-card-clicked on-card-clicked
                                 :on-card-preview on-card-preview
                                 :active-entity-id active-entity-id
                                 :on-selection-changed on-selection-changed
                                 :on-collapse-state-changed on-collapse-state-changed
                                 :all-visible-items all-visible-items
                                 :ui-state* ui-state*
                                 :drag-enabled? drag-enabled?
                                 :card-actions-fn card-actions-fn
                                 :card-status-fn card-status-fn
                                 :column-add-fn column-add-fn}]])))))

(defn apply-view-command
  "Return the state of the board after a command of the listing: collapse all cards,
  expand all cards, or clear the selection. The listing clears its own selection,
  but the cards that the board shows as selected are in the state of the board, so
  the listing sends the command to the board too. A command that is not known
  changes nothing. `command` is the map of the command, with the key `:action`."
  [{:keys [state command all-visible-items]}]
  (case (:action command)
    :collapse-all (assoc state :collapsed-ids (into #{} (keep item-id all-visible-items)))
    :expand-all (assoc state
                       :collapsed-ids #{}
                       :collapsed-columns #{})
    :clear-selection (assoc state
                            :selected-ids #{}
                            :last-selected-index nil)
    state))

(defn view
  [{:keys [kanban-id] :as props}]
  (let [ui-state* (r/atom {:selected-ids #{}
                           :collapsed-ids #{}
                           :collapsed-columns #{}
                           :last-selected-index nil})
        last-collapse-command-id* (r/atom nil)]
    (r/create-class
     {:component-did-mount
      (fn [_]
        (rf/dispatch [::event/init props]))

      :component-did-update
      (fn [this old-argv]
        (let [old-props (second old-argv)
              new-props (r/props this)
              old-config (:config old-props)
              new-config (:config new-props)]
          (when (not= old-config new-config)
            (reset! last-collapse-command-id* nil)
            (reset! ui-state* {:selected-ids #{}
                               :collapsed-ids #{}
                               :collapsed-columns #{}
                               :last-selected-index nil})
            (rf/dispatch [::event/init new-props]))))

      :component-will-unmount
      (fn [_]
        (rf/dispatch [::event/destroy {:kanban-id kanban-id}]))

      :reagent-render
      (fn [{:keys [kanban-id collapse-command on-card-clicked on-card-preview active-entity-id on-selection-changed on-collapse-state-changed
                   card-actions-fn card-status-fn column-add-fn drop-targets-fn on-create-first-item] :as _props}]
        (let [config @(rf/subscribe [::sub/config kanban-id])
              ;; A query or a toolbar filter tells apart "no match" from "nothing
              ;; yet" in the empty state, see `kanban-board`.
              filtered? (boolean (or (not (string/blank? (:query-text config)))
                                     (seq (:toolbar-filters-by-field-path config))))
              group-by-field (:group-by-field config)
              row-group-by-field (:row-group-by-field config)
              has-group-by-field? (some? group-by-field)
              card-config @(rf/subscribe [::sub/card-config kanban-id])
              cards-by-column @(rf/subscribe [::sub/cards-by-column kanban-id])
              grouping-draggable? @(rf/subscribe [::sub/grouping-draggable? kanban-id])
              read-only-user? (session/is-read-only-user?)
              drag-enabled? (and (not read-only-user?)
                                 grouping-draggable?)
              columns-status @(rf/subscribe [::sub/columns-status kanban-id])
              row-groups-status @(rf/subscribe [::sub/row-groups-status kanban-id])
              initialized? (and (some? card-config)
                                (not= columns-status :idle)
                                (or (nil? row-group-by-field)
                                    (not= row-groups-status :idle)))
              show-read-only-hint? (and has-group-by-field?
                                        (not drag-enabled?))
              all-visible-items (->> (vals (or cards-by-column {}))
                                     (mapcat (fn [column-state]
                                               (or (:items column-state) [])))
                                     vec)
              collapse-command-id (:id collapse-command)]
          (when (and collapse-command-id
                     (not= collapse-command-id @last-collapse-command-id*))
            (reset! last-collapse-command-id* collapse-command-id)
            (swap! ui-state* (fn [state]
                               (apply-view-command {:state             state
                                                    :command           collapse-command
                                                    :all-visible-items all-visible-items}))))
          [:div
           {:class "relative flex flex-auto min-h-0 flex-col gap-3"}

           (when show-read-only-hint?
             [:div
              {:class "text-xs text-base-light-text-paler"}
              (loc/tr :grid-cell-read-only)])

           (when initialized?
             [board-total {:kanban-id    kanban-id
                           :row-grouped? (some? row-group-by-field)}])

           [:div
            {:class "flex flex-auto min-h-0"}
            (cond
              ;; `config` arrives from ::event/init after mount, so it is nil on
              ;; the first renders. Judging group-by before it lands reported a
              ;; slow load as a broken listing - and the loading branch below
              ;; could never cover that window, because `initialized?` needs
              ;; card-config, which only exists once config already does.
              (and (some? config)
                   (not has-group-by-field?))
              [:div {:class "px-4 py-6 text-sm text-base-light-text-paler"}
               (loc/tr :kanban-not-configured-group-by-missing)]

              (not initialized?)
              [:div {:class "px-4 py-6 text-sm text-base-light-text-paler"}
               (loc/tr :kanban-loading-cards)]

              :else
              ^{:key (str "kanban-board-" (hash card-config))}
              [kanban-board {:kanban-id kanban-id
                             :cards-by-column cards-by-column
                             :hide-empty-columns? (:hide-empty-columns? config)
                             :row-group-by-field row-group-by-field
                             :card-config card-config
                             :on-card-clicked on-card-clicked
                             :on-card-preview on-card-preview
                             :active-entity-id active-entity-id
                             :on-selection-changed on-selection-changed
                             :on-collapse-state-changed on-collapse-state-changed
                             :all-visible-items all-visible-items
                             :ui-state* ui-state*
                             :drag-enabled? drag-enabled?
                             :card-actions-fn card-actions-fn
                             :card-status-fn card-status-fn
                             :column-add-fn column-add-fn
                             :filtered? filtered?
                             :on-create-first-item on-create-first-item}])]

           [outcome-tray/container
            {:ui-state*       ui-state*
             :drop-targets-fn drop-targets-fn}]]))})))
